import { useCallback, useEffect, useRef, useState } from "react";
import {
  clampMapZoom,
  MAP_PAN_BUTTON,
  MAP_ZOOM_STEP,
  MIN_MAP_ZOOM,
  pannedScrollOffset,
  zoomedScrollOffset,
} from "./map-zoom.js";

/**
 * Zoom per Mausrad und Pan per gedrückter Mitteltaste für eine Karte in einem
 * Scroll-Container (`.qv-map-scroll`). Gemeinsam für Quickview-Renderer
 * (`MapView`) und Editor (`EditorCanvas`), damit sich beide gleich bedienen.
 *
 * Gezoomt wird nicht per Stage-Transform, sondern über eine größere Stage
 * (`side * mapZoom`); der Container scrollt den sichtbaren Ausschnitt.
 */
export function useMapZoomPan({
  side,
  enabled = true,
  resetKey,
}: {
  side: number;
  enabled?: boolean;
  resetKey: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mapZoom, setMapZoom] = useState(MIN_MAP_ZOOM);
  // Pan-Startwerte im Ref statt State — ein Re-Render pro Maus-Pixel würde das
  // Ziehen ausbremsen. State nur für den Cursor.
  const panRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    left: number;
    top: number;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const renderZoom = enabled ? mapZoom : MIN_MAP_ZOOM;

  const applyMapZoom = useCallback(
    (requestedZoom: number) => {
      const nextZoom = clampMapZoom(requestedZoom);
      if (nextZoom === mapZoom) return;
      const scroll = scrollRef.current;
      const currentContentSize = side * mapZoom;
      const nextContentSize = side * nextZoom;
      const nextLeft = scroll
        ? zoomedScrollOffset(
            scroll.scrollLeft,
            scroll.clientWidth,
            currentContentSize,
            nextContentSize,
          )
        : 0;
      const nextTop = scroll
        ? zoomedScrollOffset(
            scroll.scrollTop,
            scroll.clientHeight,
            currentContentSize,
            nextContentSize,
          )
        : 0;
      setMapZoom(nextZoom);
      requestAnimationFrame(() => {
        if (!scroll) return;
        scroll.scrollLeft = nextLeft;
        scroll.scrollTop = nextTop;
      });
    },
    [mapZoom, side],
  );

  // Reacts `onWheel` ist passiv — `preventDefault` würde ignoriert und das Rad
  // zusätzlich den Container scrollen. Deshalb ein nativer, nicht-passiver
  // Listener.
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!enabled || !scroll) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      applyMapZoom(
        mapZoom + (event.deltaY < 0 ? MAP_ZOOM_STEP : -MAP_ZOOM_STEP),
      );
    };
    scroll.addEventListener("wheel", onWheel, { passive: false });
    return () => scroll.removeEventListener("wheel", onWheel);
  }, [applyMapZoom, enabled, mapZoom]);

  useEffect(() => {
    setMapZoom(MIN_MAP_ZOOM);
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
      scrollRef.current.scrollTop = 0;
    }
  }, [resetKey]);

  function endPan(event: React.PointerEvent<HTMLDivElement>) {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    panRef.current = null;
    setIsPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  const scrollProps = enabled
    ? {
        ref: scrollRef,
        className: `qv-map-scroll${isPanning ? " is-panning" : ""}`,
        onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
          if (event.button !== MAP_PAN_BUTTON) return;
          // Unterdrückt den nativen Autoscroll des Browsers.
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          panRef.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            left: event.currentTarget.scrollLeft,
            top: event.currentTarget.scrollTop,
          };
          setIsPanning(true);
        },
        onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
          const pan = panRef.current;
          if (!pan || pan.pointerId !== event.pointerId) return;
          event.currentTarget.scrollLeft = pannedScrollOffset(
            pan.left,
            pan.x,
            event.clientX,
          );
          event.currentTarget.scrollTop = pannedScrollOffset(
            pan.top,
            pan.y,
            event.clientY,
          );
        },
        onPointerUp: endPan,
        onPointerCancel: endPan,
        onLostPointerCapture: endPan,
        onAuxClick: (event: React.MouseEvent<HTMLDivElement>) => {
          if (event.button === MAP_PAN_BUTTON) event.preventDefault();
        },
      }
    : {};

  const contentProps = enabled
    ? {
        className: "qv-map-scroll-content",
        style: {
          width: `max(100%, ${side * renderZoom}px)`,
          height: `max(100%, ${side * renderZoom}px)`,
        },
      }
    : {};

  return { mapZoom, renderZoom, applyMapZoom, scrollProps, contentProps };
}
